import { Component, computed, inject, Input, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';

import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { startWith, distinctUntilChanged } from 'rxjs';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DataField } from '../models/data-field';
import { DataFieldGroup } from '../models/data-field-group';
import { DataFieldType } from '../models/data-field-type';
import { DocumentTypeDeveloper } from '../models/document-type';
import { TrackingField } from '../models/tracking-field';
import { DeveloperDataFieldComponent } from '../developer-data-field/developer-data-field.component';
import { XPathComponent } from 'src/app/controls/components/advanced/xpath/xpath.component';
import { ButtonComponent } from '../../controls/components/basic/button/button.component';
import { ButtonPrimaryDirective } from 'src/app/controls/directives/button-primary.directive';
import { ButtonSecondaryDirective } from 'src/app/controls/directives/button-secondary.directive';
import { ButtonSubmitDirective } from 'src/app/controls/directives/button-submit.directive';
import { SingleselectboxComponent } from 'src/app/controls/components/basic/singleselectbox/singleselectbox.component';
import { TextboxComponent } from 'src/app/controls/components/basic/textbox/textbox.component';

interface DistroDisplay {
    DistributionObjectsId: number;
    DisplayName: string;
}

type TrackingFieldTypeId = 'xpath' | 'contextproperty' | 'macro';
interface TrackingFieldTypeOption {
  id: TrackingFieldTypeId;
  name: string;
}

@Component({
  selector: 'app-developer-tracking-field',
  templateUrl: './developer-tracking-field.component.html',
  styleUrls: ['./developer-tracking-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    DeveloperDataFieldComponent,
    XPathComponent,
    ButtonComponent,
    ButtonPrimaryDirective,
    ButtonSecondaryDirective,
    ButtonSubmitDirective,
    SingleselectboxComponent,
    TextboxComponent
],
})
export class DeveloperTrackingFieldComponent implements OnInit {
  private fb = inject(FormBuilder);
  private modal = inject(NgbActiveModal);

  // Backing signal (do not bind to this directly from the outside)
  private readonly _trackingField = signal<TrackingField | null>(null);

  @Input()
  set trackingField(value: TrackingField | null) {
    this._trackingField.set(value);
    this.patchFromTrackingField(value);
  }
  get trackingField(): TrackingField | null {
    return this._trackingField();
  }

  trackingFieldTypeOptions = signal<TrackingFieldTypeOption[]>([
    { id: 'xpath', name: 'Xpath' },
    { id: 'contextproperty', name: 'Context Property' },
    { id: 'macro', name: 'Macro' },
  ]);

  selectedType = signal<TrackingFieldTypeId>('xpath');

  selectedTypeOption = computed(() =>
    this.trackingFieldTypeOptions().find(o => o.id === this.selectedType()) ?? null
  );

  @Input() distros: DocumentTypeDeveloper[];
  @Input() dataFieldGroups: DataFieldGroup[];
  @Input() dataFieldTypes: DataFieldType[];

  formData: FormGroup = this.buildForm();

  distroDisplays: DistroDisplay[];
  documentTypeId = signal<number>(null);

  ngOnInit(): void {
    const typeCtrl = this.formData.get('type') as FormControl<TrackingFieldTypeId>;

    typeCtrl.valueChanges
      .pipe(
        startWith(typeCtrl.value ?? 'xpath'),
        distinctUntilChanged()
      )
      .subscribe(v => {
        const type = (v ?? 'xpath') as TrackingFieldTypeId;
        this.selectedType.set(type);
        this.applyTypeBehavior(type); // <-- reset non-selected to null
      });

    const fc: FormControl<number> = this.formData.controls['distributionObjectId'] as FormControl<number>;
    fc.valueChanges.pipe(startWith(fc.value)).subscribe(value => this.documentTypeId.set(value));

    this.distroDisplays = (this.distros ?? []).map(e => ({
      DistributionObjectsId: e.Id,
      DisplayName: e.Name + (e.Description && e.Description.length > 1 ? ` - ${e.Description}` : ''),
    } as DistroDisplay));

    this.patchFromTrackingField(this._trackingField());
  }

  private inferType(tf: TrackingField | null): TrackingFieldTypeId {
    if (!tf) return 'xpath';
    if (tf.Macro && tf.Macro.trim().length > 0) return 'macro';
    if (
      (tf.ContextProperty && tf.ContextProperty.trim().length > 0) ||
      (tf.ContextPropertyNamespace && tf.ContextPropertyNamespace.trim().length > 0)
    ) return 'contextproperty';
    return 'xpath';
  }

  private patchFromTrackingField(tf: TrackingField | null): void {
    if (!tf) return;

    const inferredType = this.inferType(tf);

    this.formData.patchValue(
      {
        name: tf.Name ?? '',
        type: inferredType,
        xpath: tf.XPath ?? '',
        contextProperty: tf.ContextProperty ?? '',
        contextPropertyNamespace: inferredType === 'contextproperty' ? (tf.ContextPropertyNamespace ?? '') : (tf.ContextPropertyNamespace ?? null),
        macro: tf.Macro ?? '',
        showInSearchPreview: tf.ShowInSearchPreview ?? false,
        distributionObjectId: tf.DistributionObjectsId ?? null,
        dataField: {
          dataFieldGroupsId: tf.DataField?.DataFieldGroupsId ?? null,
          dataTypeId: tf.DataField?.DataTypesId ?? null,
          displayName: tf.DataField?.DisplayName ?? '',
          isRequired: tf.DataField?.IsRequired ?? false,
          dataLength: tf.DataField?.DataLength ?? null,
          regex: tf.DataField?.Regex ?? '',
          regexText: tf.DataField?.RegexErrorMessage ?? '',
          infoText: tf.DataField?.InfoText ?? '',
          defaultValue: tf.DataField?.DefaultValue ?? '',
          sortOrder: tf.DataField?.SortOrder ?? null,
          choices: tf.DataField?.Choices ?? null,
        },
      },
      { emitEvent: false }
    );

    this.selectedType.set(inferredType);
    this.applyTypeBehavior(inferredType); 
  }

  private applyTypeBehavior(type: TrackingFieldTypeId): void {
    const xpath = this.formData.get('xpath') as FormControl<string | null>;
    const contextProperty = this.formData.get('contextProperty') as FormControl<string | null>;
    const contextPropertyNamespace = this.formData.get('contextPropertyNamespace') as FormControl<string | null>;
    const macro = this.formData.get('macro') as FormControl<string | null>;

    const setNullAndDisable = (c: FormControl<any>) => {
      if (!c) return;
      c.setValue(null, { emitEvent: false });
      c.disable({ emitEvent: false });
      c.updateValueAndValidity({ emitEvent: false });
    };

    const enable = (c: FormControl<any>) => {
      c?.enable({ emitEvent: false });
      c?.updateValueAndValidity({ emitEvent: false });
    };

    const setRequired = (c: FormControl<any>, required: boolean) => {
      if (!c) return;
      if (required) c.addValidators(Validators.required);
      else c.removeValidators(Validators.required);
      c.updateValueAndValidity({ emitEvent: false });
    };

    const ensureEmptyStringIfNull = (c: FormControl<string | null>) => {
      if (!c) return;
      if (c.value === null || c.value === undefined) {
        c.setValue('', { emitEvent: false });
        c.updateValueAndValidity({ emitEvent: false });
      }
    };

    setRequired(xpath, false);
    setRequired(contextProperty, false);
    setRequired(macro, false);
    setRequired(contextPropertyNamespace, false); 

    if (type === 'xpath') {
      enable(xpath);
      setRequired(xpath, true);

      setNullAndDisable(contextProperty);
      setNullAndDisable(contextPropertyNamespace);
      setNullAndDisable(macro);
      return;
    }

    if (type === 'contextproperty') {
      setNullAndDisable(xpath);

      enable(contextProperty);
      enable(contextPropertyNamespace);

      ensureEmptyStringIfNull(contextPropertyNamespace);

      setRequired(contextProperty, true);
      setRequired(contextPropertyNamespace, true); 

      setNullAndDisable(macro);
      return;
    }

    // macro
    setNullAndDisable(xpath);
    setNullAndDisable(contextProperty);
    setNullAndDisable(contextPropertyNamespace);

    enable(macro);
    setRequired(macro, true);
  }

  buildForm(): FormGroup {
    const tf = this._trackingField();
    const inferredType = this.inferType(tf);

    return this.fb.group({
      name: [tf?.Name ?? '', Validators.required],
      type: this.fb.nonNullable.control<TrackingFieldTypeId>(inferredType, Validators.required),

      xpath: [tf?.XPath ?? null],
      contextProperty: [tf?.ContextProperty ?? null],
      contextPropertyNamespace: [inferredType === 'contextproperty' ? (tf?.ContextPropertyNamespace ?? '') : (tf?.ContextPropertyNamespace ?? null)],
      macro: [tf?.Macro ?? null],

      showInSearchPreview: [tf?.ShowInSearchPreview ?? false],
      distributionObjectId: [tf?.DistributionObjectsId ?? null, Validators.min(0)],
      dataField: this.fb.nonNullable.control({
        dataFieldGroupsId: tf?.DataField?.DataFieldGroupsId ?? null,
        dataTypeId: tf?.DataField?.DataTypesId ?? null,
        displayName: tf?.DataField?.DisplayName ?? '',
        isRequired: tf?.DataField?.IsRequired ?? false,
        dataLength: tf?.DataField?.DataLength ?? null,
        regex: tf?.DataField?.Regex ?? '',
        regexText: tf?.DataField?.RegexErrorMessage ?? '',
        infoText: tf?.DataField?.InfoText ?? '',
        defaultValue: tf?.DataField?.DefaultValue ?? '',
        sortOrder: tf?.DataField?.SortOrder ?? null,
        choices: tf?.DataField?.Choices ?? null,
      }),
    });
  }

  setType(t: TrackingFieldTypeId): void {
    (this.formData.get('type') as FormControl<TrackingFieldTypeId>).setValue(t);
  }

  ok(): void {
    if (!this.formData.valid) return;

    const v = this.formData.value;
    const existing = this._trackingField();
    const type = v.type as TrackingFieldTypeId;

    const df: DataField = {
      DataFieldsId: existing?.DataField?.DataFieldsId ?? 0,
      DataTypesId: v.dataField.dataTypeId,
      DisplayName: v.dataField.displayName,
      IsRequired: v.dataField.isRequired,
      DataLength: v.dataField.dataLength,
      DefaultValue: v.dataField.defaultValue,
      Regex: v.dataField.regex,
      RegexErrorMessage: v.dataField.regexText,
      DataFieldGroupsId: v.dataField.dataFieldGroupsId,
      SortOrder: v.dataField.sortOrder,
      InfoText: v.dataField.infoText,
      Choices: v.dataField.choices,
    };

    const tf: TrackingField = {
      TrackingFieldTypesId: existing?.TrackingFieldTypesId ?? 0,
      Name: v.name,
      XPath: type === 'xpath' ? v.xpath : null,
      ContextProperty: type === 'contextproperty' ? v.contextProperty : null,
      ContextPropertyNamespace: type === 'contextproperty' ? (v.contextPropertyNamespace ?? '') : null,
      Macro: type === 'macro' ? v.macro : null,
      ShowInSearchPreview: v.showInSearchPreview,
      DistributionObjectsId: v.distributionObjectId,
      DataField: df,
      CreatedBy: existing?.CreatedBy,
      CreatedDate: existing?.CreatedDate,
      UpdatedBy: existing?.UpdatedBy,
      UpdatedDate: existing?.UpdatedDate,
    };

    this.modal.close(tf);
  }

  cancel(): void {
    this.modal.dismiss();
  }
}
